<script lang="ts">import { tick } from 'svelte';
// Need to use let because the selectedIndex value is changed by events and you cannot use the $props() rune twice
/* eslint-disable prefer-const */
let { colorMode = 'light', items, name, selectedIndex = $bindable(-1), isOpen = false, showItemValuesInLabel = false, defaultFocusFirstOption = false, onSelect = () => { }, onExit = () => { }, multiSelect = false, } = $props();
/* eslint-enable prefer-const */
const selectOptions = [];
let currentFocusIndex = -1;
// Keep track if the dropdown was open or not for MultiSelect because there needs to be additional logic to prevent
// it from changing the focused option
let wasOpen = false;
// Use a Set to track the selected items for MultiSelectField to be able to easily update the items and turn into an
// array
const selectedItemSet = $derived(new Set(Array.isArray(selectedIndex) ? selectedIndex : []));
// Handle keydown events inside the dropdown list.
function handleDropdownKeydown(e) {
    e.preventDefault();
    switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
            e.stopPropagation();
            const direction = e.key === 'ArrowDown' ? 1 : -1;
            const newIndex = Math.max(0, Math.min(currentFocusIndex + direction, selectOptions.length - 1));
            if (newIndex !== currentFocusIndex) {
                currentFocusIndex = newIndex;
                selectOptions[currentFocusIndex].focus();
            }
            break;
        }
        case 'Enter':
        case ' ': {
            e.target?.click();
            break;
        }
        case 'Escape':
        case 'Tab': {
            onExit && onExit();
            break;
        }
        default: {
            break;
        }
    }
}
// Focus the selected option or the first option if no selection is found
function focusOption(index = 0) {
    tick().then(() => {
        if (selectOptions.length > 0 && selectOptions[index]) {
            selectOptions[index].focus();
            currentFocusIndex = index;
        }
    });
}
function handleItemClick(index) {
    // For MultiSelectField or multiple options
    if (multiSelect) {
        // Add/remove from the Set
        if (selectedItemSet.has(index)) {
            selectedItemSet.delete(index);
        }
        else {
            selectedItemSet.add(index);
        }
        // Convert the Set to an array
        const selectedItems = Array.from(selectedItemSet);
        selectedIndex = selectedItems;
        // Call the callback function with the selected items for MultiSelectField (that uses SelectField) to use
        onSelect?.(selectedItems);
    }
    else {
        // For SelectField, PhoneField, or a single option
        selectedIndex = index;
        onSelect?.(selectedIndex);
    }
}
$effect(() => {
    if (isOpen && !wasOpen) {
        if (multiSelect) {
            defaultFocusFirstOption && focusOption(0);
        }
        else {
            focusOption(defaultFocusFirstOption && selectedIndex === -1 ? 0 : selectedIndex);
        }
        wasOpen = true;
    }
    else if (!isOpen) {
        wasOpen = false;
    }
});
</script>

<!-- Need a svelte-ignore because of role. There would be no error if this was just role="group" or role="radiogroup"
 instead of being a ternary -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<div
  class="SelectDropdown"
  role={multiSelect ? 'group' : 'radiogroup'}
  tabindex="0"
  onkeydown={handleDropdownKeydown}
  data-is-open={isOpen}
  data-color-mode={colorMode}
  aria-multiselectable={multiSelect}
>
  {#each items as item, index}
    {@const isItemSelected = multiSelect ? selectedItemSet.has(index) : selectedIndex === index}
    <div
      id="option-{name}-{index}"
      class="SelectDropdown__option"
      role={multiSelect ? 'checkbox' : 'radio'}
      tabindex="-1"
      {...multiSelect ? { 'aria-checked': isItemSelected } : { 'aria-selected': isItemSelected }}
      bind:this={selectOptions[index]}
      onclick={() => handleItemClick(index)}
      onfocus={() => {
        currentFocusIndex = index;
      }}
    >
      {#if item.icon}
        <span class="SelectDropdown__option-icon">
          <item.icon />
        </span>
      {/if}
      {#if showItemValuesInLabel && item.value}
        <span class="SelectDropdown__option-value">{item.value}</span>
      {/if}
      <label for="option-{name}-{index}">
        {item.label}
      </label>
    </div>
  {/each}
</div>

<style>.SelectDropdown__option-value,
.SelectDropdown__option label {
  font-weight: var(--form-field-input-font-weight);
  font-size: var(--form-field-input-font-size);
  letter-spacing: var(--form-field-input-letter-spacing);
  line-height: var(--form-field-input-line-height);
}

/**
 * Checkbox styling is shared between CheckboxesField and MultiSelectField
 * The biggest difference between the two is the hover/focus/etc states are on different elements, so it cannot just be
 * one big mixin and has to be broken up
 */
@layer design-system-components.pwds {
  .SelectDropdown {
    --color: var(--color-text);
    position: absolute;
    top: calc(100% + var(--select-dropdown-top-offset));
    left: 0;
    z-index: 200;
    max-height: var(--select-dropdown-max-height);
    width: 100%;
    background-color: var(--color-background);
    border-radius: var(--select-dropdown-border-radius);
    box-shadow: 0 0 0 1px var(--color-form-field-default);
    color-scheme: var(--select-dropdown-scroll-color);
    overflow-y: auto;
    visibility: hidden;
    opacity: 0;
  }
  .SelectDropdown[data-is-open=true] {
    visibility: visible;
    opacity: 1;
  }
  .SelectDropdown__option {
    padding: var(--space-20) var(--select-dropdown-option-padding-horizontal);
    line-height: var(--text-variant-100-line-height);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--space-20);
    align-self: stretch;
    cursor: pointer;
    position: relative;
    transition: background-color var(--duration-quick) var(--easing-decelerate);
  }
  .SelectDropdown__option:not(:first-child)::after {
    content: "";
    position: absolute;
    top: 0;
    left: var(--select-dropdown-option-padding-horizontal);
    right: var(--select-dropdown-option-padding-horizontal);
    height: 1px;
    background-color: var(--select-dropdown-option-border-color);
    transition: opacity var(--duration-quick) var(--easing-decelerate);
  }
  .SelectDropdown__option[aria-selected=true] {
    border-left: 3px var(--border-line-style) var(--select-dropdown-option-checked-border-color);
  }
  .SelectDropdown__option:hover, .SelectDropdown__option:focus-visible {
    outline: none;
    background-color: var(--select-dropdown-option-hover-background);
  }
  .SelectDropdown__option:hover::after, .SelectDropdown__option:hover + .SelectDropdown__option::after, .SelectDropdown__option:focus-visible::after, .SelectDropdown__option:focus-visible + .SelectDropdown__option::after {
    opacity: 0;
  }
  .SelectDropdown__option-icon {
    display: flex;
    align-items: center;
  }
  .SelectDropdown__option-value,
  .SelectDropdown__option label {
    cursor: pointer;
  }
  .SelectDropdown__option-value {
    color: var(--select-dropdown-option-value-color);
    width: var(--select-dropdown-option-value-width);
  }
}</style>
